/**
 * Compact UI Language Button Component
 * A small button in the header showing current language ISO code with dropdown menu
 */

import Dropdown from 'bootstrap/js/src/dropdown.js';
import type { IUIManager } from './ui-manager.types';
import type { LanguageCode } from '../core/language-constants';
import { logger } from '../core/logger';

export interface UILanguageButtonOptions {
  uiManager?: IUIManager;
  cssClass?: string;
}

class UILanguageButton {
  private uiManager?: IUIManager;
  private cssClass: string;
  private buttonElement: HTMLButtonElement | null;
  private dropdownElement: HTMLUListElement | null;

  constructor(options: UILanguageButtonOptions = {}) {
    this.uiManager = options.uiManager;
    this.cssClass = options.cssClass || 'ui-language-button';

    this.buttonElement = null;
    this.dropdownElement = null;
  }

  /**
   * Initialize the UI language button
   * @param button - Button element or selector
   */
  initialize(button: HTMLElement | string): void {
    // Get button element
    if (typeof button === 'string') {
      this.buttonElement = document.querySelector(button) as HTMLButtonElement;
    } else {
      this.buttonElement = button as HTMLButtonElement;
    }

    if (!this.buttonElement) {
      throw new Error('UI language button element not found');
    }

    if (!this.uiManager) {
      throw new Error('UI Manager is required for UI language button');
    }

    this.setupButton();
    this.createDropdown();
    this.attachEventListeners();
    this.updateButtonText();

    // Listen for external language changes
    this.uiManager.onLanguageChange((_newLanguage: LanguageCode) => {
      this.updateButtonText();
      this.updateDropdownOptions();
    });
  }

  /**
   * Set up the button element
   */
  setupButton(): void {
    if (!this.buttonElement) return;

    this.buttonElement.classList.add(this.cssClass);
    // Note: Bootstrap collapse attributes are already in HTML
    // No need to set them programmatically
  }

  /**
   * Create dropdown menu
   */
  createDropdown(): void {
    if (!this.buttonElement) return;

    // Find the dropdown menu - it should be a sibling with .dropdown-menu class
    const parent = this.buttonElement.parentElement;
    if (parent && parent.classList.contains('dropdown')) {
      this.dropdownElement = parent.querySelector('.dropdown-menu');

      if (!this.dropdownElement) {
        // Create dropdown menu if it doesn't exist
        this.dropdownElement = document.createElement('ul');
        this.dropdownElement.className = 'dropdown-menu dropdown-menu-end';
        this.dropdownElement.id = 'ui-language-options';
        parent.appendChild(this.dropdownElement);
      }
    } else {
      logger.error('Button must be inside a .dropdown container');
    }

    this.populateDropdown();
  }

  /**
   * Populate dropdown menu with language options
   */
  populateDropdown(): void {
    if (!this.dropdownElement || !this.uiManager) return;

    this.dropdownElement.innerHTML = '';

    const languages = this.uiManager.getLanguageOptions();
    const currentLanguage = this.uiManager.getCurrentLanguage();

    languages.forEach((lang) => {
      const li = document.createElement('li');

      const option = document.createElement('button');
      option.className = 'dropdown-item';
      option.textContent = `${lang.code.toUpperCase()} - ${lang.nativeName}`;
      option.setAttribute('type', 'button');
      option.setAttribute('data-language', lang.code);

      if (lang.code === currentLanguage) {
        option.classList.add('active');
        option.setAttribute('aria-current', 'true');
      }

      option.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleLanguageSelect(lang.code);
      });

      li.appendChild(option);
      if (this.dropdownElement) {
        this.dropdownElement.appendChild(li);
      }
    });
  }

  /**
   * Update dropdown options (mark active language)
   */
  updateDropdownOptions(): void {
    if (!this.dropdownElement || !this.uiManager) return;

    const currentLanguage = this.uiManager.getCurrentLanguage();
    const options = this.dropdownElement.querySelectorAll('.dropdown-item');

    options.forEach((option) => {
      const langCode = option.getAttribute('data-language');
      if (langCode === currentLanguage) {
        option.classList.add('active');
        option.setAttribute('aria-current', 'true');
      } else {
        option.classList.remove('active');
        option.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Attach event listeners
   */
  attachEventListeners(): void {
    // Bootstrap handles all dropdown behavior automatically via data attributes
    // No manual event listeners needed for show/hide
  }

  /**
   * Close dropdown (handled automatically by Bootstrap)
   */
  closeDropdown(): void {
    // Bootstrap handles this via data-bs-toggle="dropdown"
  }

  /**
   * Handle language selection
   * @param languageCode - Selected language code
   */
  async handleLanguageSelect(languageCode: LanguageCode): Promise<void> {
    if (!this.uiManager || !this.buttonElement) return;

    const currentLanguage = this.uiManager.getCurrentLanguage();

    if (languageCode !== currentLanguage) {
      // Close the menu first: Bootstrap does not hide a dropdown whose toggle
      // is disabled, and loading a language can take a network round trip
      // (#133), so the menu stayed open and the next click only "closed" it.
      Dropdown.getInstance(this.buttonElement)?.hide();
      try {
        // Disable button during language change
        this.buttonElement.disabled = true;
        this.buttonElement.textContent = '...';

        // Change UI language
        await this.uiManager.setLanguage(languageCode);

        logger.debug(`UI language changed to: ${languageCode}`);
      } catch (error) {
        logger.error('Failed to change UI language:', error);
        // Revert button text on error
        this.updateButtonText();
      } finally {
        // Re-enable button
        this.buttonElement.disabled = false;
      }
    }

    // Dropdown closes automatically when item is clicked (Bootstrap default behavior)
  }

  /**
   * Update button text with current language ISO code
   */
  updateButtonText(): void {
    if (!this.uiManager || !this.buttonElement) return;

    const currentLanguage = this.uiManager.getCurrentLanguage();
    this.buttonElement.textContent = currentLanguage.toUpperCase();

    // Update title with language name
    const langConfig = this.uiManager.getSupportedLanguages()[currentLanguage];
    if (langConfig) {
      this.buttonElement.title = `Interface Language: ${langConfig.nativeName}`;
    }
  }

  /**
   * Get current UI language code
   * @returns Current language code
   */
  getCurrentLanguage(): LanguageCode | undefined {
    return this.uiManager?.getCurrentLanguage();
  }

  /**
   * Enable/disable the button
   * @param enabled - Whether to enable the button
   */
  setEnabled(enabled: boolean): void {
    if (!this.buttonElement) return;
    this.buttonElement.disabled = !enabled;
  }

  /**
   * Show/hide the button
   * @param visible - Whether to show the button
   */
  setVisible(visible: boolean): void {
    if (!this.buttonElement) return;
    this.buttonElement.style.display = visible ? '' : 'none';
  }

  /**
   * Destroy the UI language button
   */
  destroy(): void {
    // Bootstrap dropdown is handled automatically, no need to dispose

    if (this.dropdownElement) {
      this.dropdownElement.remove();
    }

    if (this.buttonElement) {
      this.buttonElement.classList.remove(this.cssClass);
    }

    this.buttonElement = null;
    this.dropdownElement = null;
  }

  /**
   * Refresh the button (useful after language changes)
   */
  refresh(): void {
    this.updateButtonText();
    this.populateDropdown();
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.UILanguageButton = UILanguageButton;
}

export default UILanguageButton;
